import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import {RumInit} from '@maersk-global/telemetry-web-sdk'
import telemetryConfig, {LogLevel} from '@/config/telemetry'
import {setTelemetryInstance} from '@/telemetry'

declare global {
  interface Window {
    Faro?: ReturnType<typeof RumInit>
  }
}

interface TelemetryContextType {
  log: (level: LogLevel, message: string[]) => void
}

const TelemetryContext = createContext<TelemetryContextType | undefined>(
  undefined,
)

export const TelemetryProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [telemetry, setTelemetry] = useState<ReturnType<typeof RumInit> | null>(
    null,
  )

  useEffect(() => {
    if (typeof window !== 'undefined' && !window.Faro) {
      const initializedTelemetry = RumInit({
        app: {
          name: telemetryConfig.app.name,
          version: telemetryConfig.app.version ?? '',
          environment: telemetryConfig.app.environment,
        },
        apiKey: telemetryConfig.apiKey,
        performanceInstrument: {ignoreEvents: ['msal.*']},
        traceLog: false,
        useSendBeacon: true,
      })

      window.Faro = initializedTelemetry
      setTelemetry(initializedTelemetry)
    }
  }, [])

  const log = useCallback(
    (level: LogLevel, message: string[]) => {
      if (telemetry?.api) {
        telemetry.api.pushLog(message, {level})
      } else {
        console.warn('Telemetria não inicializada!')
      }
    },
    [telemetry],
  )

  useEffect(() => {
    setTelemetryInstance(log)
  }, [log])

  return (
    <TelemetryContext.Provider value={{log}}>
      {children}
    </TelemetryContext.Provider>
  )
}

export const useTelemetry = () => {
  const context = useContext(TelemetryContext)
  if (!context) {
    throw new Error('useTelemetry must be used within a TelemetryProvider')
  }
  return context
}
